import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

// Get base URL with /api prefix
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API_URL = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Context for optimized image mappings
const OptimizedImageContext = createContext({
  mappings: {},
  loading: true,
  getOptimizedUrl: () => null,
  getOptimizedData: () => null,
});

// Global cache to prevent duplicate fetches across re-renders
let globalMappingsCache = null;
let globalFetchPromise = null;

/**
 * Provider component that fetches and caches image mappings
 */
export const OptimizedImageProvider = ({ children }) => {
  const [mappings, setMappings] = useState(globalMappingsCache || {});
  const [loading, setLoading] = useState(!globalMappingsCache);
  const fetchedRef = useRef(false);

  useEffect(() => {
    // Skip if already fetched or cached
    if (fetchedRef.current || globalMappingsCache) {
      if (globalMappingsCache && Object.keys(mappings).length === 0) {
        setMappings(globalMappingsCache);
        setLoading(false);
      }
      return;
    }
    
    fetchedRef.current = true;

    const fetchMappings = async () => {
      // Reuse existing fetch promise if in progress
      if (globalFetchPromise) {
        const data = await globalFetchPromise;
        setMappings(data);
        setLoading(false);
        return;
      }

      try {
        globalFetchPromise = fetch(`${API_URL}/images/mapping`)
          .then(res => res.ok ? res.json() : { mappings: {} })
          .then(data => {
            const mappingsData = data.mappings || {};
            globalMappingsCache = mappingsData;
            window.__OPTIMIZED_IMAGE_MAPPINGS__ = mappingsData;
            return mappingsData;
          });
        
        const data = await globalFetchPromise;
        setMappings(data);
      } catch (error) {
        console.warn('Failed to fetch image mappings:', error);
      } finally {
        setLoading(false);
        globalFetchPromise = null;
      }
    };

    fetchMappings();
  }, [mappings]);

  /**
   * Get optimized URL for original image URL
   * @param {string} originalUrl - Original image URL
   * @param {object} options - Options { format: 'webp'|'avif'|'jpeg', width: number, thumbnail: number }
   */
  const getOptimizedUrl = (originalUrl, options = {}) => {
    if (!originalUrl) return originalUrl;
    
    const { format = 'webp', width, thumbnail } = options;
    
    // Helper to build full URL - avoid double /api prefix
    const buildUrl = (path) => {
      if (!path) return null;
      return path.startsWith('/api/') 
        ? `${BACKEND_URL}${path}` 
        : `${API_URL}${path}`;
    };
    
    // If we have pre-optimized mappings, use them
    if (mappings[originalUrl]) {
      const imageData = mappings[originalUrl];

      // If thumbnail requested
      if (thumbnail) {
        const thumbs = imageData.thumbnails?.[format] || [];
        const thumb = thumbs.find(t => t.size === thumbnail) || thumbs[thumbs.length - 1];
        if (thumb) {
          return buildUrl(thumb.url);
        }
      }

      // If specific width requested
      if (width) {
        const variants = imageData.variants?.[format] || [];
        // Find closest width
        const variant = variants.reduce((closest, v) => {
          if (!closest) return v;
          const closestDiff = Math.abs(closest.width - width);
          const currentDiff = Math.abs(v.width - width);
          return currentDiff < closestDiff ? v : closest;
        }, null);
        if (variant) {
          return buildUrl(variant.url);
        }
      }

      // Return primary
      const primary = imageData.primary?.[format];
      if (primary) return buildUrl(primary);
    }
    
    // No pre-optimized mapping - use the image proxy for on-the-fly optimization
    // Only proxy external URLs (not local/API URLs)
    if (originalUrl.startsWith('http') && !originalUrl.includes(BACKEND_URL)) {
      const targetWidth = thumbnail || width || 400;
      const targetFormat = format === 'jpeg' ? 'jpeg' : 'webp';
      const quality = 85;
      return `${API_URL}/images/proxy?url=${encodeURIComponent(originalUrl)}&w=${targetWidth}&q=${quality}&fmt=${targetFormat}`;
    }
    
    return originalUrl; // Fallback to original
  };

  /**
   * Get full optimized data for building <picture> elements
   */
  const getOptimizedData = (originalUrl) => {
    if (!originalUrl || !mappings[originalUrl]) {
      return null;
    }

    const data = mappings[originalUrl];
    
    // Add full URLs to variants - avoid double /api prefix
    const addBaseUrl = (variants) => {
      if (!variants) return [];
      return variants.map(v => ({
        ...v,
        // If URL starts with /api, use BACKEND_URL directly, otherwise use API_URL
        url: v.url.startsWith('/api/') 
          ? `${BACKEND_URL}${v.url}` 
          : `${API_URL}${v.url}`,
      }));
    };
    
    // Helper to build full URL for primary images
    const buildUrl = (path) => {
      if (!path) return null;
      return path.startsWith('/api/') 
        ? `${BACKEND_URL}${path}` 
        : `${API_URL}${path}`;
    };

    return {
      id: data.id,
      primary: {
        avif: buildUrl(data.primary?.avif),
        webp: buildUrl(data.primary?.webp),
        jpeg: buildUrl(data.primary?.jpeg),
        width: data.primary?.width,
        height: data.primary?.height,
      },
      variants: {
        avif: addBaseUrl(data.variants?.avif),
        webp: addBaseUrl(data.variants?.webp),
        jpeg: addBaseUrl(data.variants?.jpeg),
      },
      thumbnails: {
        avif: addBaseUrl(data.thumbnails?.avif),
        webp: addBaseUrl(data.thumbnails?.webp),
        jpeg: addBaseUrl(data.thumbnails?.jpeg),
      },
      dominantColor: data.dominant_color,
    };
  };

  return (
    <OptimizedImageContext.Provider value={{ mappings, loading, getOptimizedUrl, getOptimizedData }}>
      {children}
    </OptimizedImageContext.Provider>
  );
};

/**
 * Hook to access optimized image utilities
 */
export const useOptimizedImages = () => {
  return useContext(OptimizedImageContext);
};

/**
 * Smart Product Image component that automatically uses optimized versions
 */
export const SmartProductImage = ({ 
  src, 
  alt, 
  size = 400,
  className = '',
  priority = false,
  ...props 
}) => {
  const { getOptimizedData, loading } = useOptimizedImages();
  
  // Get optimized data
  const optimizedData = getOptimizedData(src);
  
  // Build srcset string
  const buildSrcset = (variants) => {
    if (!variants || variants.length === 0) return '';
    return variants.map(v => `${v.url} ${v.width}w`).join(', ');
  };

  // If we have optimized data, use picture element
  if (optimizedData && !loading) {
    const useThumb = size <= 400;
    const avifSrcset = buildSrcset(useThumb ? optimizedData.thumbnails.avif : optimizedData.variants.avif);
    const webpSrcset = buildSrcset(useThumb ? optimizedData.thumbnails.webp : optimizedData.variants.webp);
    const jpegSrcset = buildSrcset(useThumb ? optimizedData.thumbnails.jpeg : optimizedData.variants.jpeg);
    
    const jpegVariants = useThumb ? optimizedData.thumbnails.jpeg : optimizedData.variants.jpeg;
    const fallbackSrc = jpegVariants?.[jpegVariants.length - 1]?.url || src;
    
    const sizes = `(max-width: 640px) 50vw, ${size}px`;

    return (
      <picture>
        {avifSrcset && (
          <source type="image/avif" srcSet={avifSrcset} sizes={sizes} />
        )}
        {webpSrcset && (
          <source type="image/webp" srcSet={webpSrcset} sizes={sizes} />
        )}
        <img
          src={fallbackSrc}
          srcSet={jpegSrcset || undefined}
          sizes={jpegSrcset ? sizes : undefined}
          alt={alt}
          width={size}
          height={size}
          loading={priority ? 'eager' : 'lazy'}
          decoding={priority ? 'sync' : 'async'}
          fetchPriority={priority ? 'high' : 'auto'}
          className={className}
          style={{ backgroundColor: optimizedData.dominantColor }}
          {...props}
        />
      </picture>
    );
  }

  // Fallback to regular image
  return (
    <img
      src={src}
      alt={alt}
      width={size}
      height={size}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      className={className}
      {...props}
    />
  );
};

export default OptimizedImageContext;
